// src/contexts/CurrencyContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../services/firebase/config';

const CurrencyContext = createContext(null);

export const CURRENCY_FLAGS = {
  USD: { 
    flag: 'usd', 
    name: 'US Dollar',
    symbol: '$'
  },
  EUR: { 
    flag: 'eur', 
    name: 'Euro',
    symbol: '€'
  },
  GBP: { 
    flag: 'gbp', 
    name: 'UK Pound Sterling',
    symbol: '£'
  },
  JPY: { 
    flag: 'jpy', 
    name: 'Japanese Yen',
    symbol: '¥'
  },
  CHF: { 
    flag: 'chf', 
    name: 'Swiss Franc',
    symbol: 'CHF'
  },
  TRY: { 
    flag: 'try', 
    name: 'Turkish Lira',
    symbol: '₺'
  }
};

export function CurrencyProvider({ children }) {
  const [rates, setRates] = useState(new Map());
  const [selectedCurrency, setSelectedCurrency] = useState(() => 
    localStorage.getItem('selectedCurrency') || 'USD'
  );
  const [loading, setLoading] = useState(true);

  // Firebase'den kurları yükle ve dinle
  useEffect(() => {
    console.log('🔄 Starting currency rates subscription...');
    try {
      const ratesRef = collection(db, 'CurrencyRate');
      const q = query(ratesRef);
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        console.log(`📥 Received ${snapshot.size} currency rates`);
        
        const newRates = new Map();
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data?.rate && !isNaN(data.rate)) {
            newRates.set(data.symbol, data.rate);
            console.log(`💱 Rate loaded: ${data.symbol} = ${data.rate}`);
          }
        });

        console.log('📊 Available rates:', Object.fromEntries(newRates));
        setRates(newRates);
        setLoading(false);
      }, (error) => {
        console.error('❌ Currency subscription error:', error);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('❌ Currency setup error:', error);
      setLoading(false);
    }
  }, []);

  const findRate = useCallback((fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return 1;

    const directPair = `${fromCurrency}${toCurrency}`;
    const directRate = rates.get(directPair);
    if (directRate) return directRate;

    const inversePair = `${toCurrency}${fromCurrency}`;
    const inverseRate = rates.get(inversePair);
    if (inverseRate) return 1 / inverseRate;

    console.warn(`⚠️ No rate found for ${fromCurrency}->${toCurrency}`);
    return null;
  }, [rates]);

  const convertAmount = useCallback((amount, fromCurrency) => {
    if (!amount || amount === 0) return 0;
    if (!fromCurrency) return amount;
    if (loading) return amount;

    fromCurrency = fromCurrency.toUpperCase();
    const toCurrency = selectedCurrency.toUpperCase();

    if (fromCurrency === toCurrency) return amount;

    const rate = findRate(fromCurrency, toCurrency);
    if (!rate) return amount;

    return amount * rate;
  }, [selectedCurrency, loading, findRate]);

  const formatAmount = useCallback((amount, currency = selectedCurrency) => {
    if (amount === null || amount === undefined) return '-';
    
    try {
      const options = {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: currency === 'JPY' ? 0 : 2,
        maximumFractionDigits: currency === 'JPY' ? 0 : 2
      };
      
      const formatter = new Intl.NumberFormat('en-US', options);

      // TRY için özel sembol işleme
      if (currency === 'TRY') {
        return `${formatter.format(amount).replace('TRY', '₺')}`;
      }

      return formatter.format(amount);
    } catch (error) {
      console.error('Error formatting amount:', error);
      return `${amount} ${currency}`;
    }
  }, [selectedCurrency]);

  const getFlag = useCallback((currency) => {
    const flagCode = CURRENCY_FLAGS[currency]?.flag;
    return flagCode ? `/images/currencys/${flagCode}.png` : null;
  }, []);

  const getCurrencyName = useCallback((currency) => {
    return CURRENCY_FLAGS[currency]?.name || currency;
  }, []);

  useEffect(() => {
    localStorage.setItem('selectedCurrency', selectedCurrency);
    console.log(`🔄 Currency changed to ${selectedCurrency}`);
  }, [selectedCurrency]);

  return (
    <CurrencyContext.Provider value={{
      selectedCurrency,
      setSelectedCurrency,
      convertAmount,
      formatAmount,
      getFlag,
      getCurrencyName,
      rates: Object.fromEntries(rates),
      loading
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
